import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

/**
 * Mobile-audit / E2E test sign-in. Reads ?token from the URL and POSTs
 * it to /api/auth/dev-token (which only registers when the api has
 * LEXCRM_AUDIT_BYPASS_TOKEN env set). Mints a Firebase custom token
 * for the audit user and signs in. Strips the token from the URL on
 * success.
 *
 * Production-safe: if the api isn't compiled with the env var, the
 * endpoint 404s and this page surfaces a clear error.
 */
export function DevAuth() {
  const navigate = useNavigate();
  const { signInWithCustom } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    if (!token) {
      setError('Missing ?token query param');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth/dev-token', {
          method: 'POST',
          headers: { 'X-Dev-Auth': token },
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(`dev-token endpoint returned ${res.status}: ${body}`);
        }
        const data = (await res.json()) as { customToken: string };
        await signInWithCustom(data.customToken);
        if (cancelled) return;
        // Strip token from URL so it doesn't leak into history/screenshots
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.pathname);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, signInWithCustom]);

  return (
    <main className="signin-wrap">
      <div className="signin-card">
        <h1 className="brand">LexCRM</h1>
        <p className="brand-sub">audit sign-in</p>
        {error ? (
          <div className="signin-error">{error}</div>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text2)' }}>signing in…</p>
        )}
      </div>
    </main>
  );
}
